import { getEnvironment, noop } from "@sail/utils";
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { getApolloContext, useDataContext } from "../../../internal/revamp/apollo/operationContext";
import useGraphQlDataClient from "../../../internal/revamp/config/useGraphQlDataClient";
import GqlOperationError from "../../../internal/revamp/graphql/GqlOperationError";
import { getQueryName } from "../../../internal/revamp/graphql/documentUtils/query";
import ObservableFragment from "../../../internal/revamp/observable/ObservableFragment";
import ObservableQuery from "../../../internal/revamp/observable/ObservableQuery";
import addTypename from "../../../internal/revamp/queryUtils/addTypename";
import { getOperationName } from "../../../internal/shared/utils/gql";
import { skip } from "../../../internal/shared/utils/skip";
import getUnexpectedErrors from "./getUnexpectedErrors";
const fallbackResult = {
    data: null,
    errors: null,
    isPending: false
};
function getObservableOrThrow({ observableStore, queryDoc, variables }) {
    const env = getEnvironment();
    const fragmentObservable = observableStore.getObservableFragment({
        document: queryDoc,
        variables
    });
    if (fragmentObservable) {
        return fragmentObservable;
    }
    if (env === 'development') {
        const fragmentName = getQueryName(queryDoc);
        throw new Error(`${fragmentName} hasn't been used in a rootQuery before. Please make sure to include it in this route's rootQuery`);
    }
    return observableStore.createObservableQuery({
        document: queryDoc,
        variables
    });
}
/**
 *
 * @see https://sail.stripe.me/data/api/unstable_useFragment
 */ export default function useFragment(/**
   * Fragment options, including GraphQL fragment document, variables and token. Can be omitted by passing `skip` from `@sail/data`.
   */ options) {
    const isSkip = options === skip;
    const queryDocument = isSkip ? undefined : options.doc;
    const queryDocWithTypename = useMemo(()=>queryDocument ? addTypename(queryDocument) : undefined, [
        queryDocument
    ]);
    const expectedErrorsRef = useRef(isSkip ? [] : options.expectedErrors);
    const expectedErrorsFromOptions = isSkip ? undefined : options.expectedErrors;
    useEffect(()=>{
        expectedErrorsRef.current = isSkip ? [] : expectedErrorsFromOptions;
    }, [
        expectedErrorsFromOptions,
        isSkip
    ]);
    const component = isSkip ? undefined : options.component;
    const dataContext = useDataContext();
    const shouldUseSuspense = dataContext.shouldUseSuspense;
    const operationName = isSkip || !queryDocWithTypename ? 'CouldNotFindQueryName' : getOperationName(queryDocWithTypename) || 'CouldNotFindQueryName';
    const dataClient = useGraphQlDataClient();
    const observableStore = dataClient.observableStore;
    const observable = isSkip || !queryDocWithTypename ? null : getObservableOrThrow({
        observableStore,
        queryDoc: queryDocWithTypename,
        variables: options.variables
    });
    // Create apolloLinkContext inside a callback to use the latest value
    // of expectedErrors while not having to include it in the dependency
    // array and cause subscribeCb/refetch instability.
    const createApolloLinkContext = useCallback(()=>getApolloContext(dataContext, {
            type: 'STANDALONE_QUERY',
            operationName,
            expectedErrors: expectedErrorsRef.current || []
        }, component), [
        dataContext,
        operationName,
        component
    ]);
    const subscribeCb = useCallback((reactCb)=>{
        if (!observable) {
            return noop;
        }
        const apolloLinkContext = createApolloLinkContext();
        const { unsubscribe } = observable.subscribe({
            next: reactCb,
            error: reactCb
        }, apolloLinkContext);
        return unsubscribe;
    }, [
        observable,
        createApolloLinkContext
    ]);
    const result = useSyncExternalStore(subscribeCb, ()=>isSkip || !observable ? fallbackResult : observable.getCurrentResult());
    const variablesFromOptions = isSkip ? undefined : options.variables;
    const refetch = useCallback(()=>{
        if (isSkip) {
            return;
        }
        if (observable instanceof ObservableQuery) {
            const apolloLinkContext = createApolloLinkContext();
            observable.refetch(apolloLinkContext);
        } else if (observable instanceof ObservableFragment) {
            observableStore.refetchSingleFragment({
                document: queryDocWithTypename,
                variables: variablesFromOptions
            });
        }
    }, [
        observable,
        observableStore,
        queryDocWithTypename,
        variablesFromOptions,
        isSkip,
        createApolloLinkContext
    ]);
    if (result.errors !== null) {
        const unexpectedErrors = getUnexpectedErrors(result.errors, expectedErrorsFromOptions || []);
        if (unexpectedErrors) {
            throw new GqlOperationError(unexpectedErrors);
        }
    }
    const isRefetching = result.isPending && result.data !== null;
    if (shouldUseSuspense && !isSkip && !result.data && !result.errors) {
        if (observable instanceof ObservableFragment) {
            throw observable.startAsyncWaitOnResult();
        } else if (observable instanceof ObservableQuery) {
            const apolloLinkContext = getApolloContext(dataContext, {
                type: 'STANDALONE_QUERY',
                operationName,
                expectedErrors: expectedErrorsFromOptions || []
            }, component);
            throw observable.startAsyncWaitOnResult(apolloLinkContext);
        }
    }
    return {
        errors: result.errors,
        data: result.data,
        isRefetching,
        refetch
    };
}

//# sourceMappingURL=useFragment.js.map